import { useCallback, useEffect, useState } from 'react'

import type { Member, MemberListing } from '../lib/api'
import { api } from '../lib/api'
import type { Role } from '../lib/auth'

const ROLE_HELP: Record<Role, string> = {
  viewer: 'Leitura — abre análises prontas',
  analyst: 'Analista — cria análises e gera leituras (usa cota)',
  admin: 'Admin — tudo, e administra membros',
}

/**
 * Membros deste projeto, lidos e gravados no Hub pelo proxy do backend.
 *
 * O modo de acesso (quem entra sozinho) não aparece aqui de propósito: ele vive no
 * Hub. Esta tela cuida de pessoas — e é o único lugar onde papel se define, porque o
 * Hub concede entrada mas não grava papel.
 */
export function MembersPanel() {
  const [listing, setListing] = useState<MemberListing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    api.members
      .list()
      .then((data) => setListing(data))
      .catch((exc: Error) => setError(exc.message))
  }, [])
  useEffect(load, [load])

  const act = (uid: string, run: () => Promise<unknown>) => {
    setBusy(uid)
    setError(null)
    run()
      .then(load)
      .catch((exc: Error) => setError(exc.message))
      .finally(() => setBusy(null))
  }

  if (!listing) {
    return error ? <p className="brief-error" role="alert">{error}</p> : <div className="loading-state" role="status"><span className="loading-mark" /><span>Lendo membros no Hub</span></div>
  }

  return (
    <section className="members">
      <InviteForm roles={listing.roles} onInvited={load} />
      {error && <p className="brief-error" role="alert">{error}</p>}
      <p className="members-note">
        <strong>Remover não barra:</strong> neste projeto todo <code>@galeria.ag</code> entra
        sozinho como leitura, e quem for removido volta no próximo login. Para tirar alguém de
        verdade, bloqueie.
      </p>
      <ul className="members-list">
        {listing.members.map((member) => (
          <MemberRow
            key={member.uid}
            member={member}
            roles={listing.roles}
            isMe={member.uid === listing.me}
            busy={busy === member.uid}
            onRole={(role) => act(member.uid, () => api.members.setRole(member.uid, role))}
            onBlock={() => act(member.uid, () => api.members.setBlocked(member.uid, !member.blocked))}
            onRemove={() => {
              if (window.confirm(`Remover ${member.email} do projeto?`)) {
                act(member.uid, () => api.members.remove(member.uid))
              }
            }}
          />
        ))}
      </ul>
    </section>
  )
}

function MemberRow({
  member, roles, isMe, busy, onRole, onBlock, onRemove,
}: {
  member: Member
  roles: Role[]
  isMe: boolean
  busy: boolean
  onRole: (role: Role) => void
  onBlock: () => void
  onRemove: () => void
}) {
  const display = member.name ?? member.email
  // Um papel que o Hub guardou e o código não conhece aparece como está, mas não é
  // oferecido no select: o backend recusaria.
  const known = roles.includes(member.role as Role)
  return (
    <li className={`member-row${member.blocked ? ' member-blocked' : ''}`}>
      <span className="user-initials" aria-hidden="true">{initials(display)}</span>
      <div className="member-who">
        <strong>{display}</strong>
        {member.name && <span>{member.email}</span>}
      </div>
      {member.blocked && <span className="member-badge">bloqueado</span>}
      {isMe ? (
        <span className="member-self">você · {member.role}</span>
      ) : (
        <div className="member-actions">
          <select
            value={known ? member.role : ''}
            disabled={busy}
            onChange={(event) => onRole(event.target.value as Role)}
            aria-label={`Papel de ${display}`}
          >
            {!known && <option value="" disabled>{member.role}</option>}
            {roles.map((role) => <option key={role} value={role} title={ROLE_HELP[role]}>{role}</option>)}
          </select>
          <button type="button" disabled={busy} onClick={onBlock}>
            {member.blocked ? 'Desbloquear' : 'Bloquear'}
          </button>
          <button type="button" disabled={busy} onClick={onRemove}>Remover</button>
        </div>
      )}
    </li>
  )
}

function InviteForm({ roles, onInvited }: { roles: Role[]; onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('viewer')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setSending(true)
    setError(null)
    setResult(null)
    api.members
      .invite(email, role)
      .then((out) => {
        setResult(
          out.email_sent
            ? `Convite enviado para ${email}.`
            : `Convite criado. Envie este link: ${out.invite_url ?? '(o Hub não devolveu link)'}`,
        )
        setEmail('')
        onInvited()
      })
      .catch((exc: Error) => setError(exc.message))
      .finally(() => setSending(false))
  }

  return (
    <form className="members-invite" onSubmit={submit}>
      <label>
        <span>Convidar por e-mail</span>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="pessoa@empresa.com" />
      </label>
      <label>
        <span>Papel</span>
        <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {roles.map((r) => <option key={r} value={r}>{ROLE_HELP[r]}</option>)}
        </select>
      </label>
      <button type="submit" className="primary-button" disabled={sending}>
        {sending ? 'Convidando…' : 'Convidar'}
      </button>
      {result && <p className="members-result">{result}</p>}
      {error && <p className="brief-error" role="alert">{error}</p>}
    </form>
  )
}

function initials(value: string): string {
  const parts = value.split(/[\s@.]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}
